// ─── CONTENT MODERATION ──────────────────────────────────────────────────────

const BAD_WORDS = [
  'fuck','shit','bitch','asshole','bastard','damn','crap','dick','pussy','cock',
  'nigger','nigga','faggot','retard','whore','slut','cunt',
  'كس','زب','شرموطة','عرص','خول','منيوك','ابن الشرموطة','كلب','حمار',
  'متناك','بنت الشرموطة','يلعن','لعنة',
];

const LINK_PATTERNS = [
  /https?:\/\//i, /www\./i, /\.com/i, /\.net/i, /\.org/i,
  /\.me/i, /\.io/i, /bit\.ly/i, /t\.me/i, /wa\.me/i,
  /instagram/i, /tiktok/i, /snapchat/i, /twitter/i, /facebook/i,
];

const PHONE_PATTERNS = [
  /\+\d{7,}/,
  /\b\d{3}[\s\-\.]\d{3}[\s\-\.]\d{4}\b/,
  /\b01[0-9]{9}\b/,
  /\b00\d{9,}\b/,
  /\b\d{10,11}\b/,
];

// ─── CLIENT-SIDE RATE LIMIT (first line of defense, UI only) ─────────────────
// The real rate limit is enforced server-side via the check_rate_limit()
// Postgres function. This client copy just gives immediate feedback.
const sendLog = {};

export function checkRateLimit(jarId, sessionKey) {
  const key = `${jarId}::${sessionKey}`;
  const now = Date.now();
  const windowMs = 60 * 60 * 1000;
  const maxSends = 5;

  if (!sendLog[key]) sendLog[key] = [];
  sendLog[key] = sendLog[key].filter(t => now - t < windowMs);

  if (sendLog[key].length >= maxSends) {
    return { ok: false, reason: 'rate_limit' };
  }
  sendLog[key].push(now);
  return { ok: true };
}

// ─── SERVER-SIDE RATE LIMIT CHECK ────────────────────────────────────────────
// Calls the Postgres check_rate_limit() function.
// Uses a hash of navigator.userAgent + screen resolution as a privacy-safe
// fingerprint (no IP address collected on the client).
export async function checkRateLimitServer(supabase, jarId) {
  try {
    // Build a simple browser fingerprint — not stored raw, just used as hash input
    const raw = [
      navigator.userAgent || '',
      screen.width + 'x' + screen.height,
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    ].join('|');

    // Hash it (SHA-256 via SubtleCrypto, available in all modern browsers)
    const encoder = new TextEncoder();
    const buf = await crypto.subtle.digest('SHA-256', encoder.encode(raw));
    const ipHash = Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('').slice(0, 32); // 32 hex chars is plenty

    const { data, error } = await supabase.rpc('check_rate_limit', {
      p_jar_id: jarId,
      p_ip_hash: ipHash,
    });

    if (error) {
      // If the RPC fails, fall through to client-side only (don't block send)
      console.warn('Server rate limit check failed, using client-side only');
      return { ok: true };
    }

    return { ok: data === true };
  } catch {
    return { ok: true }; // Fail open — client-side still catches obvious abuse
  }
}

export function moderateMessage(text) {
  if (!text || text.trim().length < 2) return { ok: false, reason: 'too_short' };
  if (text.trim().length > 500) return { ok: false, reason: 'too_long' };
  const lower = text.toLowerCase();
  for (const word of BAD_WORDS) {
    if (lower.includes(word.toLowerCase())) return { ok: false, reason: 'bad_word' };
  }
  for (const pattern of LINK_PATTERNS) {
    if (pattern.test(text)) return { ok: false, reason: 'link' };
  }
  for (const pattern of PHONE_PATTERNS) {
    if (pattern.test(text)) return { ok: false, reason: 'phone' };
  }
  return { ok: true };
}

export function getModerationError(reason, lang) {
  const messages = {
    en: {
      too_short: 'Message is too short. Write something meaningful! 💛',
      too_long: 'Message is too long. Keep it under 500 characters.',
      bad_word: 'Your message contains inappropriate language. Please keep it kind! 🌸',
      link: 'Links are not allowed in messages for safety reasons.',
      phone: 'Phone numbers are not allowed in messages for safety reasons.',
      rate_limit: "You've sent 5 glows to this person today — come back in an hour! 💛",
    },
    ar: {
      too_short: 'الرسالة قصيرة أوي. اكتب حاجة معناها! 💛',
      too_long: 'الرسالة طويلة أوي. خليها أقل من ٥٠٠ حرف.',
      bad_word: 'الرسالة فيها كلام مش مناسب. خليها حلوة! 🌸',
      link: 'مش مسموح بلينكات في الرسايل عشان الأمان.',
      phone: 'مش مسموح بأرقام تليفون في الرسايل عشان الأمان.',
      rate_limit: 'بعتت ٥ أضواء لنفس الشخص النهارده — ارجع بعد ساعة! 💛',
    }
  };
  return messages[lang]?.[reason] || messages.en[reason] || 'Something went wrong.';
}
